<template>
    <div class="tool-block my-2" :class="blockClass">
        <!-- Header row -->
        <div class="flex items-center gap-2 px-3 py-1.5">
            <UIcon :name="statusIcon" class="w-4 h-4 flex-shrink-0" :class="iconClass" />
            <span class="font-medium text-slate-700">{{ displayTitle }}</span>
            <span class="flex-1 min-w-0" />
            <span v-if="isToolProcessing" class="text-[11px] text-blue-500 animate-pulse whitespace-nowrap">preparing…</span>
            <span v-else-if="uploadState === 'importing'" class="text-[11px] text-blue-500 animate-pulse whitespace-nowrap">importing…</span>
            <span v-else-if="uploadState === 'done'" class="text-[11px] text-green-600 whitespace-nowrap">uploaded</span>
        </div>

        <!-- Upload card (tool output available, not yet uploaded) -->
        <div v-if="showUploadCard" class="mx-3 mb-2">
            <div class="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
                <div class="flex items-center gap-3">
                    <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <UIcon :name="uploadType === 'contacts' ? 'i-ion-people' : 'i-ion-business'" class="w-5 h-5 text-blue-600" />
                    </div>
                    <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-slate-700">Upload {{ uploadType === 'contacts' ? 'Contacts' : 'Companies' }}</p>
                        <p class="text-xs text-slate-500 mt-0.5">
                            {{ uploadType === 'contacts'
                                ? 'CSV or Excel with contact info (email, name, company, etc.)'
                                : 'CSV or Excel with company URLs' }}
                        </p>
                    </div>
                    <UButton color="primary" size="sm" :disabled="!chatId" @click="importerOpen = true">
                        <UIcon name="i-ion-cloud-upload" class="w-4 h-4 mr-1" />
                        Upload CSV / Excel
                    </UButton>
                </div>
            </div>
        </div>

        <!-- Upload result -->
        <div v-if="uploadState === 'done' && uploadResult" class="mx-3 mb-2">
            <div class="rounded-lg border border-green-200 bg-green-50 p-3">
                <div class="flex items-center gap-2">
                    <UIcon name="i-ion-checkmark-circle" class="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div>
                        <p class="text-sm font-medium text-green-800">
                            {{ uploadResult.count }} {{ uploadType === 'contacts' ? 'contacts' : 'companies' }} uploaded
                        </p>
                        <p v-if="uploadResult.fileName" class="text-xs text-green-600 mt-0.5">
                            from {{ uploadResult.fileName }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error state -->
        <div v-if="uploadState === 'error'" class="mx-3 mb-2 px-2.5 py-2 rounded-md bg-red-50 border border-red-100 text-red-600 text-xs">
            {{ uploadError }}
        </div>

        <!-- CSV Importer Modal -->
        <SharedCSVImporter
            v-if="importerOpen"
            v-model:open="importerOpen"
            :sheet="importerSheet"
            :cloud="{ targetKind: uploadType === 'contacts' ? 'contacts' : 'accounts', campaignId, chatId, chatActionId: part.toolCallId }"
            :on-complete="handleImportComplete"
            v-model:options="importOptions"
            :enable-custom-fields="true"
            :sequence-provider="uploadType === 'contacts' ? sequenceProvider : undefined"
        >
            <template v-if="uploadType === 'contacts'" #review-options>
                <CampaignEditEmailFinderOptions v-model:enrich-email="importEnrichEmail"
                    v-model:replace-invalid-email="importReplaceInvalidEmail" />
            </template>
        </SharedCSVImporter>
    </div>
</template>

<script setup lang="ts">
import type { ImportResult as CloudImportResult, ImportSessionDTO as CloudImportSession } from '@coldreach/utils/cloud-imports';

type ImportSampleRow = Record<string, string>;

const props = defineProps<{
    chatId?: string;
    onUploadComplete: (result: ChatUploadResult) => Promise<void>;
    part: {
        type: string;
        toolCallId?: string;
        state?: string;
        input?: unknown;
        output?: unknown;
        errorText?: string;
        [key: string]: unknown;
    };
}>();

type ChatUploadResult = {
        importSessionId: string;
        chatId?: string;
        tableId: number;
        count: number;
        linkedContactsCount?: number;
        companyCount?: number;
        inputRowCount?: number;
        type: 'contacts' | 'companies';
        fileName: string;
        campaignId?: number;
        signalItemId?: number;
        emailFinderOptions?: { enrichEmail: boolean; replaceInvalidEmail: boolean; verifyEmail: true };
        fieldAvailability?: ContactFieldAvailability;
        sampleRows?: ImportSampleRow[];
};

// Parse tool input/output
const partInput = computed(() => (props.part.input || {}) as Record<string, unknown>);
const partOutput = computed(() => (props.part.output || {}) as Record<string, unknown>);

const uploadType = computed(() => {
    return (partOutput.value.type || partInput.value.type || 'contacts') as 'contacts' | 'companies';
});

const campaignId = computed(() => {
    return (partOutput.value.campaignId || partInput.value.campaignId) as number | undefined;
});

// Tool state detection
const isToolProcessing = computed(() => {
    if (props.part.output !== undefined) return false;
    if (props.part.errorText) return false;
    return true;
});

const isToolCompleted = computed(() =>
    props.part.state === 'output-available' ||
    (props.part.output !== undefined && !props.part.errorText)
);

type ContactFieldAvailabilityField = {
    availableCount: number;
    missingCount: number;
    availablePercent: number;
};

type ContactFieldAvailability = {
    totalRows: number;
    importableRows: number;
    skippedRows: number;
    email: ContactFieldAvailabilityField;
    linkedinUrl: ContactFieldAvailabilityField;
    companyUrl: ContactFieldAvailabilityField;
    contactName: ContactFieldAvailabilityField;
    hasAnyEmail: boolean;
    hasAnyLinkedInUrl: boolean;
    hasAnyCompanyUrl: boolean;
    hasAnyContactName: boolean;
};

// Local upload state
const uploadState = ref<'idle' | 'importing' | 'done' | 'error'>('idle');
const uploadResult = ref<{ tableId: number; count: number; fileName: string } | null>(null);
const uploadError = ref<string>('');
const importerOpen = ref(false);
const importEnrichEmail = ref(true);
const importReplaceInvalidEmail = ref(true);

// Use the actual target campaign, not a provider guessed from chat text or the current page.
const { data: uploadSequences, status: sequencesStatus } = useAsyncData(
    () => `chat-upload-sequences-${campaignId.value ?? 'none'}`,
    async () => {
        if (!importerOpen.value || !campaignId.value) return [];
        return await $fetch<SequenceListItem[]>('/api/sequences', { query: { icpId: campaignId.value } });
    },
    { server: false, default: () => [], watch: [importerOpen] },
);
const sequenceProvider = computed(() => sequencesStatus.value === 'success' ? [...uploadSequences.value].sort((a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() || b.id - a.id,
)[0]?.provider : undefined);

const showUploadCard = computed(() =>
    isToolCompleted.value && (uploadState.value === 'idle' || uploadState.value === 'error')
);

// Display
const displayTitle = computed(() => {
    if (uploadType.value === 'contacts') return 'Upload Contacts';
    return 'Upload Companies';
});

const statusIcon = computed(() => {
    if (props.part.errorText) return 'i-heroicons-x-circle';
    if (uploadState.value === 'done') return 'i-heroicons-check-circle';
    if (uploadState.value === 'error') return 'i-heroicons-x-circle';
    if (isToolProcessing.value || uploadState.value === 'importing') return 'i-heroicons-arrow-path';
    return 'i-heroicons-arrow-up-tray';
});

const iconClass = computed(() => {
    if (props.part.errorText || uploadState.value === 'error') return 'text-red-500';
    if (uploadState.value === 'done') return 'text-green-500';
    if (isToolProcessing.value || uploadState.value === 'importing') return 'text-blue-500 animate-spin';
    return 'text-blue-500';
});

const blockClass = computed(() => {
    if (props.part.errorText || uploadState.value === 'error') return 'tool-block--error';
    if (uploadState.value === 'done') return 'tool-block--success';
    if (isToolProcessing.value || uploadState.value === 'importing') return 'tool-block--active';
    return 'tool-block--active';
});

// CSVImporter config
const importerSheet = computed(() =>
    uploadType.value === 'contacts' ? contactSheet : companySheet
);

const importOptions = computed({
    get: () => ({ enrichEmail: importEnrichEmail.value, replaceInvalidEmail: importReplaceInvalidEmail.value, verifyEmail: true }),
    set: (value: { enrichEmail?: boolean; replaceInvalidEmail?: boolean }) => {
        importEnrichEmail.value = value.enrichEmail ?? true;
        importReplaceInvalidEmail.value = value.replaceInvalidEmail ?? true;
    },
});

async function handleImportComplete(result: CloudImportResult, session: CloudImportSession) {
    if (!result.sourceListId) throw new Error('The completed import is missing its saved list. Refresh to check again.');
    const count = uploadType.value === 'contacts' ? result.count : result.linkedCount ?? result.count;
    await props.onUploadComplete({
        importSessionId: session.id,
        chatId: session.chatId ?? props.chatId,
        tableId: result.sourceListId,
        count,
        linkedContactsCount: uploadType.value === 'contacts' ? result.count : undefined,
        companyCount: result.companyCount,
        inputRowCount: session.activeRowCount,
        type: uploadType.value,
        fileName: result.fileName ?? 'Pasted data',
        campaignId: campaignId.value,
        signalItemId: result.contactSignalId,
        emailFinderOptions: uploadType.value === 'contacts' ? { enrichEmail: session.options.enrichEmail ?? true, replaceInvalidEmail: session.options.replaceInvalidEmail ?? true, verifyEmail: true } : undefined,
        fieldAvailability: result.fieldAvailability,
        sampleRows: result.sampleRows,
    });
    uploadState.value = 'done';
    uploadResult.value = { tableId: result.sourceListId, count, fileName: result.fileName ?? 'Pasted data' };
}
</script>

<style scoped>
.tool-block {
    border-left: 3px solid rgb(203 213 225);
    border-radius: 6px;
    background: rgb(248 250 252 / 0.6);
    font-size: 12px;
}

.tool-block--error {
    border-left-color: rgb(239 68 68);
    background: rgb(254 242 242 / 0.5);
}

.tool-block--active {
    border-left-color: rgb(59 130 246);
    background: rgb(239 246 255 / 0.5);
}

.tool-block--success {
    border-left-color: rgb(34 197 94);
    background: rgb(240 253 244 / 0.4);
}
</style>
